import React, { Suspense, useEffect, useMemo } from "react";
import { StatsCard } from "./StatsCard";
import { BikeList } from "./BikeList";
import { useBikeStore } from "../../store/useBikeStore";
import { useCityStore } from "../../store/useCityStore";
import { useCountryStore } from "../../store/useCountryStore";
import { useVehicleCategoryStore } from "../../store/useVehicleCategoryStore";
import { LoadingSpinner } from "../common/LoadingSpinner";
import { isTimestampWithinWindow } from "../../utils/helpers";
import {
  Bike,
  MapPin,
  Battery,
  Activity,
  AlertTriangle,
  TrendingUp,
} from "lucide-react";

const VehicleSummaryCharts = React.lazy(
  () => import("./VehicleSummaryCharts"),
);

export const Dashboard: React.FC = () => {
  const { bikes, loading, loadIfNeeded } = useBikeStore();
  const { cities, loadIfNeeded: loadCitiesIfNeeded } = useCityStore();
  const { countries, loadIfNeeded: loadCountriesIfNeeded } = useCountryStore();
  const { categories, loadIfNeeded: loadCategoriesIfNeeded } =
    useVehicleCategoryStore();

  const recentBikes = useMemo(
    () =>
      bikes
        .filter((b) => b.last_location?.timestamp)
        .sort((a, b) =>
          b.last_location!.timestamp.localeCompare(a.last_location!.timestamp),
        )
        .slice(0, 10),
    [bikes],
  );

  const bikeStats = useMemo(() => {
    const nowMs = Date.now();

    const total = bikes.length;
    const active = bikes.filter((bike) =>
      isTimestampWithinWindow(bike.last_location?.timestamp, undefined, nowMs),
    ).length;
    const inactive = bikes.filter(
      (bike) =>
        !isTimestampWithinWindow(bike.last_location?.timestamp, undefined, nowMs),
    ).length;
    const maintenance = bikes.filter(
      (bike) => bike.status === "maintenance",
    ).length;
    const withGpsData = bikes.filter((bike) => !!bike.last_location).length;
    const lowBattery = bikes.filter((bike) => {
      const battery = bike.last_location?.battery_level;
      return typeof battery === "number" && battery < 20;
    }).length;
    const moving = bikes.filter(
      (bike) => (bike.last_location?.speed ?? 0) > 0.5,
    ).length;

    return {
      total,
      active,
      inactive,
      maintenance,
      withGpsData,
      lowBattery,
      moving,
    };
  }, [bikes]);

  useEffect(() => {
    loadIfNeeded();
    loadCitiesIfNeeded().catch(console.error);
    loadCountriesIfNeeded().catch(console.error);
    loadCategoriesIfNeeded().catch(console.error);
  }, [
    loadIfNeeded,
    loadCitiesIfNeeded,
    loadCountriesIfNeeded,
    loadCategoriesIfNeeded,
  ]);

  if (loading && !bikes.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Bikes"
          value={bikeStats.total}
          icon={Bike}
          color="blue"
          loading={loading}
        />
        <StatsCard
          title="Active Bikes"
          value={bikeStats.active}
          icon={Activity}
          color="green"
          loading={loading}
        />
        <StatsCard
          title="Currently Moving"
          value={bikeStats.moving}
          icon={MapPin}
          color="purple"
          // change={`+${Math.round(
          //   (bikeStats?.moving / (bikeStats?.active || 1)) * 100
          // )}%`}
        />
        <StatsCard
          title="Low Battery"
          value={bikeStats.lowBattery}
          icon={Battery}
          color="red"
          loading={loading}
        />
      </div>

      {/* Additional Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatsCard
          title="In Maintenance"
          value={bikeStats.maintenance}
          icon={AlertTriangle}
          color="yellow"
          loading={loading}
        />
        <StatsCard
          title="With GPS Data"
          value={bikeStats.withGpsData}
          icon={TrendingUp}
          color="indigo"
          loading={loading}
        />
        <StatsCard
          title="Success Rate"
          value={`${Math.round(
            (bikeStats.withGpsData / (bikeStats.total || 1)) * 100,
          )}%`}
          icon={Activity}
          color="green"
          loading={loading}
        />
      </div>

      <Suspense
        fallback={
          <div className="bg-white rounded-lg shadow p-8 flex items-center justify-center">
            <LoadingSpinner size="lg" />
          </div>
        }
      >
        <VehicleSummaryCharts
          bikes={bikes}
          cities={cities}
          countries={countries}
          categories={categories}
          loading={loading}
        />
      </Suspense>

      {/* Recent Vehicles List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-2 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent Vehicles</h3>
        </div>
        <BikeList bikes={recentBikes} loading={loading} />
      </div>
    </div>
  );
};
